import { useState, useRef } from 'react';
import { Button } from '@mui/material';
import { H6, Body3 } from '../../themes/typography';
import ImageUpload from '../../assets/images/ImageUpload.svg';
import { errorToast } from '../../utils/toasts/toasts';
import '../../styles/components/ImageFileInput.css';

const ImageFileInput = ({
    onFileSelect,
    onFileRemove,
    acceptedFormats = 'image/jpeg,image/png,image/jpg',
    maxSize = 5, // MB
    recommendedDimensions = '439px x 247px',
    className = '',
    disabled = false,
    label = 'Click to Upload Front Image',
    imageInputStyle = {},
    mainInputContainer = {},
    preview
}) => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [isDragOver, setIsDragOver] = useState(false);
    const fileInputRef = useRef(null);

    function validateFile(file) {
        const validTypes = ['image/jpeg', 'image/png', 'image/jpg'];

        if (!validTypes.includes(file.type)) {
            errorToast('Only JPEG, PNG and JPG files are supported');
            return false;
        }

        if (file.size > maxSize * 1024 * 1024) {
            errorToast(`Image size should be less than ${maxSize}MB`);
            return false;
        }

        return true;
    }

    const handleFileSelect = async (file) => {
        if (!validateFile(file)) return;

        setIsUploading(true);

        const reader = new FileReader();
        reader.onload = (e) => {
            setPreviewUrl(e.target.result);
            setSelectedFile(file);
            setIsUploading(false);
            onFileSelect?.(file);
        };
        reader.readAsDataURL(file);
    };

    const handleInputChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            handleFileSelect(file);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragOver(false);
        const file = e.dataTransfer.files[0];
        if (file) {
            handleFileSelect(file);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragOver(false);
    };

    const handleClick = () => {
        if (!disabled && !isUploading) {
            fileInputRef.current?.click();
        }
    };

    const handleChange = () => {
        fileInputRef.current?.click();
    };

    const handleRemove = () => {
        setSelectedFile(null);
        setPreviewUrl(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        onFileRemove?.();
    };

    return (
        <div className={`image-file-input ${className}`} style={mainInputContainer}>
            <input
                ref={fileInputRef}
                type="file"
                accept={acceptedFormats}
                onChange={handleInputChange}
                className="image-file-input__hidden"
                disabled={disabled}
            />

            <div
                className={`image-file-input__container ${
                    isDragOver ? 'drag-over' : ''
                } ${disabled ? 'disabled' : ''} ${selectedFile ? 'has-file' : ''}`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onClick={!selectedFile ? handleClick : undefined}
                style={imageInputStyle}
            >
                {/* Upload State */}
                {!selectedFile && !isUploading && (
                    <div className="image-file-input__upload-area">
                        <div className="thumbnail-upload-svg">
                            <img src={ImageUpload} alt="Thumbnail" />
                        </div>
                        <div className="image-file-input-text">
                            <H6>{label}</H6>
                            <Body3>JPEG or PNG smaller than 10 MB allowed</Body3>
                            <Body3>{recommendedDimensions}</Body3>
                        </div>
                    </div>
                )}

                {/* Loading State */}
                {isUploading && (
                    <div className="image-file-input__loading">
                        <div className="image-file-input__spinner">
                            <div className="spinner"></div>
                        </div>
                        <p className="image-file-input__loading-text">Uploading...</p>
                    </div>
                )}

                {/* Preview State */}
                {(preview || previewUrl) && !isUploading && (
                    <div className="image-file-input__preview">
                        <img
                            src={preview || previewUrl || '/placeholder.svg'}
                            alt="Preview"
                            className="image-file-input__preview-image"
                        />
                    </div>
                )}
            </div>

            {/* Action Buttons - Only show when file is selected */}
            {selectedFile && !isUploading && (
                <div className="image-file-input__actions">
                    <Button
                        variant="text"
                        onClick={handleRemove}
                        disabled={disabled}
                        sx={{
                            color: 'var(--error-main-color)'
                        }}
                    >
                        Remove
                    </Button>
                    <Button variant="text" onClick={handleChange} disabled={disabled}>
                        Change
                    </Button>
                </div>
            )}
        </div>
    );
};

export default ImageFileInput;
